import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AggregationBucket, LedgerQuery, PropertyValuationInput } from '@app/shared';
import {
  addPropertyValuation,
  fetchAggregations,
  fetchAnnualSummary,
  fetchConsolidatedPatrimoine,
  fetchForecast,
  fetchPropertyPatrimoine,
  fetchPropertyValuations,
  fetchTransactions,
  generateAdvisory,
} from './api';

export const transactionsQueryOptions = (filters: LedgerQuery) =>
  queryOptions({ queryKey: ['transactions', filters] as const, queryFn: () => fetchTransactions(filters) });

export function useTransactions(filters: LedgerQuery) {
  return useQuery(transactionsQueryOptions(filters));
}

export function useAggregations(filters: { bucket: AggregationBucket; propertyId?: number; dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['ledger-aggregations', filters] as const,
    queryFn: () => fetchAggregations(filters),
  });
}

export function useAnnualSummary(year: number, propertyId?: number) {
  return useQuery({
    queryKey: ['annual-summary', year, propertyId] as const,
    queryFn: () => fetchAnnualSummary(year, propertyId),
  });
}

export function usePropertyValuations(propertyId: number) {
  return useQuery({
    queryKey: ['property-valuations', propertyId] as const,
    queryFn: () => fetchPropertyValuations(propertyId),
  });
}

export function usePropertyPatrimoine(propertyId: number) {
  return useQuery({
    queryKey: ['property-patrimoine', propertyId] as const,
    queryFn: () => fetchPropertyPatrimoine(propertyId),
  });
}

export function useConsolidatedPatrimoine() {
  return useQuery({ queryKey: ['consolidated-patrimoine'] as const, queryFn: fetchConsolidatedPatrimoine });
}

export function useForecast(propertyId?: number) {
  return useQuery({ queryKey: ['forecast', propertyId] as const, queryFn: () => fetchForecast(propertyId) });
}

export function useGenerateAdvisory() {
  return useMutation({ mutationFn: (propertyId: number | undefined) => generateAdvisory(propertyId) });
}

export function useAddPropertyValuation(propertyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PropertyValuationInput) => addPropertyValuation(propertyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-valuations', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property-patrimoine', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['consolidated-patrimoine'] });
    },
  });
}
